import { styled } from "@mui/material/styles";
import {
    Box,
    Drawer,
    IconButton,
    Typography,
    Avatar,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Badge,
    Backdrop,
} from "@mui/material";
import { Apps as AppsIcon } from "@mui/icons-material";
import {
    DRAWER_WIDTH,
    COLLAPSED_DRAWER_WIDTH,
    APP_TOP_BAR_HEIGHT,
} from "../utilities/GenericLayoutUtils";

// Re-export layout constants for existing imports from this module
export { DRAWER_WIDTH, COLLAPSED_DRAWER_WIDTH, APP_TOP_BAR_HEIGHT };
export const MOBILE_BREAKPOINT = "md"; // Use md as breakpoint for mobile/desktop

// MAIN LAYOUT CONTAINER
export const LayoutContainer = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "auto 1fr", // Sidebar auto-width, content takes remaining
    gridTemplateRows: "100vh", // Full viewport height
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden",
    backgroundColor: theme.palette.background.default,

    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
        gridTemplateColumns: "1fr", // Single column on mobile
        gridTemplateRows: "100vh",
    },
}));

// SIDEBAR CONTAINER
export const SidebarContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "visible", // Allow button to stick out
    position: "relative",
    zIndex: theme.zIndex.drawer,

    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
        display: "none", // Hidden on mobile (handled by mobile drawer)
    },
}));

export const DesktopDrawer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    width: open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[3],
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    height: "100vh",
    overflow: "visible", // Allow button to stick out
    display: "flex",
    flexDirection: "column",
    position: "relative", // Ensure button positions relative to this
}));

export const MobileDrawer = styled(Drawer)(({ theme }) => ({
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
        display: "block",
    },
    [theme.breakpoints.up(MOBILE_BREAKPOINT)]: {
        display: "none",
    },
    "& .MuiDrawer-paper": {
        width: DRAWER_WIDTH,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
        height: "100vh", // Ensure mobile drawer also takes full height
    },
}));

// FIXED: Same height as GenericHeader so green bar aligns with HPRMS brand block
export const DrawerHeader = styled("div", {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 2),
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    height: APP_TOP_BAR_HEIGHT,
    minHeight: APP_TOP_BAR_HEIGHT,
    maxHeight: APP_TOP_BAR_HEIGHT,
    boxSizing: "border-box",
    position: "relative",
    flexShrink: 0,
}));

export const LogoSection = styled(Box, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center", // Always center content
    gap: theme.spacing(1.5),
    width: "100%",
}));

export const LogoText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    fontWeight: 500,
    color: theme.palette.primary.contrastText,
    fontSize: "1.4rem", // Increased size
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    opacity: open ? 1 : 0,
    width: open ? "auto" : 0,
    overflow: "hidden",
    transition: theme.transitions.create(["opacity", "width"], {
        duration: theme.transitions.duration.short,
    }),
}));

export const LogoIcon = styled(AppsIcon)(({ theme }) => ({
    fontSize: "1.5rem",
    color: theme.palette.primary.contrastText,
}));

export const ToggleButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    position: "absolute",
    // Align with center of DrawerHeader (APP_TOP_BAR_HEIGHT)
    top: "50%",
    right: -20,
    transform: "translateY(-50%)",
    zIndex: 1200,
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    width: 40,
    height: 40,
    borderRadius: "50%",
    boxShadow: theme.shadows[4],
    border: `2px solid ${theme.palette.background.paper}`,
    transition: theme.transitions.create(["background-color", "transform"], {
        duration: theme.transitions.duration.short,
    }),
    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        transform: "translateY(-50%) scale(1.05)",
    },
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
        display: "none",
    },
}));

export const UserSection = styled(Box, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    transition: theme.transitions.create(["padding", "gap"], {
        duration: theme.transitions.duration.short,
    }),
    ...(open
        ? {}
        : {
            padding: theme.spacing(1),
            justifyContent: "center",
            gap: 0,
        }),
}));

export const UserAvatar = styled(Avatar)(({ theme }) => ({
    width: 40,
    height: 40,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: "1rem",
    fontWeight: 600,
    border: `2px solid ${theme.palette.divider}`,
}));

export const UserInfo = styled(Box, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    flex: 1,
    opacity: open ? 1 : 0,
    transition: theme.transitions.create(["opacity"], {
        duration: theme.transitions.duration.short,
    }),
    ...(open
        ? {}
        : {
            width: 0,
            overflow: "hidden",
            display: "none", // <--- Ensures absolutely no width is taken
        }),
}));

export const SectionTitle = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    padding: theme.spacing(1.5, 2, 0.5, 2),
    fontSize: "0.7rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    opacity: open ? 1 : 0,
    transition: theme.transitions.create(["opacity"], {
        duration: theme.transitions.duration.short,
    }),
    ...(open
        ? {}
        : {
            height: 0,
            padding: 0,
        }),
}));

// DRAWER FOOTER - Fixed at bottom
export const DrawerFooter = styled(Box)(({ theme }) => ({
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    zIndex: 1,
}));

// SCROLLABLE MENU CONTENT - This ensures proper scrolling within sidebar
export const MenuContent = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: theme.spacing(15),
    "&::-webkit-scrollbar": {
        width: "4px",
    },
    "&::-webkit-scrollbar-track": {
        background: theme.palette.divider,
    },
    "&::-webkit-scrollbar-thumb": {
        background: theme.palette.text.disabled,
        borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: theme.palette.primary.main,
    },
}));

export const StyledListItem = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== "active" && prop !== "open",
})(({ theme, active, open }) => ({
    margin: theme.spacing(0.25, 1),
    borderRadius: 8,
    transition: theme.transitions.create(
        ["background-color", "transform", "box-shadow"],
        {
            duration: theme.transitions.duration.short,
        },
    ),
    position: "relative",
    overflow: "hidden",
    textDecoration: "none",
    color: "inherit",
    minHeight: 40,
    ...(open
        ? {}
        : {
            margin: theme.spacing(0.25, "auto"),
            justifyContent: "center",
            paddingLeft: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
            borderRadius: 8,
            width: "fit-content",
        }),
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
        transform: "translateX(2px)",
        "& .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
        },
        "& .MuiListItemText-primary": {
            color: theme.palette.text.primary,
            fontWeight: 600,
        },
    },
    ...(active && {
        backgroundColor: theme.palette.action.selected,
        "& .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
        },
        "& .MuiListItemText-primary": {
            color: theme.palette.text.primary,
            fontWeight: 600,
        },
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: theme.palette.primary.main,
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
        },
    }),
}));

export const StyledListItemIcon = styled(ListItemIcon, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    minWidth: open ? 48 : "auto",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(["color", "min-width"], {
        duration: theme.transitions.duration.short,
    }),
}));

export const StyledListItemText = styled(ListItemText, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    margin: 0,
    "& .MuiTypography-root": {
        fontSize: "0.875rem",
        fontWeight: 500,
        color: theme.palette.text.primary,
        lineHeight: 0,
    },
    opacity: open ? 1 : 0,
    transition: theme.transitions.create(["opacity"], {
        duration: theme.transitions.duration.short,
    }),
    ...(open
        ? {}
        : {
            width: 0,
            overflow: "hidden",
        }),
}));

export const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
        backgroundColor: "#FF5722",
        color: theme.palette.primary.contrastText,
        fontSize: "0.7rem",
        fontWeight: 600,
        minWidth: 16,
        height: 16,
        border: `1px solid ${theme.palette.background.paper}`,
    },
}));

// MOBILE MENU BUTTON
export const MobileMenuButton = styled(IconButton)(({ theme }) => ({
    position: "fixed",
    top: 16,
    left: 16,
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: theme.shadows[4],
    "&:hover": {
        backgroundColor: theme.palette.primary.dark,
    },
    [theme.breakpoints.up(MOBILE_BREAKPOINT)]: {
        display: "none",
    },
}));

// CLOSE BUTTON FOR MOBILE
export const MobileCloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.contrastText,
    marginLeft: "auto",
    [theme.breakpoints.up(MOBILE_BREAKPOINT)]: {
        display: "none",
    },
}));

// MAIN CONTENT - Properly integrated with grid layout
export const MainContent = styled(Box)(({ theme }) => ({
    gridColumn: "2 / 3", // Takes second grid column on desktop
    height: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
    backgroundColor: theme.palette.background.default,
    minWidth: 0, // Allow grid child to shrink so header can use full width
    width: "100%",
    WebkitOverflowScrolling: "touch",
    // Make sticky GenericHeader stick to the top of this scroll area
    position: "relative",

    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
        gridColumn: "1 / 2", // Takes full width on mobile
        marginLeft: 0,
        width: "100%",
    },
}));

// BACKDROP FOR MOBILE DRAWER
export const CustomBackdrop = styled(Backdrop)(({ theme }) => ({
    zIndex: theme.zIndex.drawer - 1,
    [theme.breakpoints.up(MOBILE_BREAKPOINT)]: {
        display: "none",
    },
}));
